import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Star, Image as ImageIcon, X, Check, ArrowUp, ArrowDown } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MediaEditor } from "@/components/MediaEditor";
import { CoverMedia } from "@/components/CoverMedia";
import { HomeLayoutEditor } from "@/components/HomeLayoutEditor";
import { ColorPicker, colorHex } from "@/lib/calendarColors";
import { useAuth } from "@/context/AuthContext";

const EMPTY_SERVICE = { color_id: "", title: "", category: "", description: "", price: "", hourly_rate: 0, image: "", images: [], media_types: [], posters: [], active: true, order: 0 };
const EMPTY_PORTFOLIO = { title: "", category: "", image: "", images: [], media_types: [], posters: [], featured: false, order: 0 };
const withMedia = (x) => ({ ...x, images: x.images?.length ? x.images : [x.image].filter(Boolean), media_types: x.images?.length ? x.media_types || [] : [], posters: x.images?.length ? x.posters || [] : [] });

export default function ManageContent() {
  return (
    <div data-testid="admin-content">
      <h1 className="font-display text-3xl text-white mb-6">Manage Content</h1>
      <Tabs defaultValue="services">
        <TabsList className="bg-[#111] border border-white/10">
          <TabsTrigger value="services" data-testid="tab-services">Services</TabsTrigger>
          <TabsTrigger value="portfolio" data-testid="tab-portfolio">Portfolio</TabsTrigger>
          <TabsTrigger value="home" data-testid="tab-home-layout">Home Page</TabsTrigger>
        </TabsList>
        <TabsContent value="services" className="mt-6"><ServicesTab /></TabsContent>
        <TabsContent value="portfolio" className="mt-6"><PortfolioTab /></TabsContent>
        <TabsContent value="home" className="mt-6"><HomeLayoutEditor /></TabsContent>
      </Tabs>
    </div>
  );
}

function ServicesTab() {
  const qc = useQueryClient();
  const { admin } = useAuth();
  const isSuper = admin?.role === "super_admin";
  const [editing, setEditing] = useState(null);
  const { data: items = [] } = useQuery({ queryKey: ["services-admin"], queryFn: () => api.get("/services?include_inactive=true").then((r) => r.data) });

  const save = async () => {
    try {
      if (editing.id) await api.put(`/services/${editing.id}`, editing);
      else await api.post("/services", editing);
      qc.invalidateQueries({ queryKey: ["services-admin"] });
      qc.invalidateQueries({ queryKey: ["services"] });
      setEditing(null);
      toast.success("Saved");
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete service?")) return;
    await api.delete(`/services/${id}`);
    qc.invalidateQueries({ queryKey: ["services-admin"] });
    qc.invalidateQueries({ queryKey: ["services"] });
    toast.success("Deleted");
  };
  const move = async (idx, dir) => {
    const arr = [...items];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    await api.post("/services/reorder", arr.map((s) => s.id));
    qc.invalidateQueries({ queryKey: ["services-admin"] });
    qc.invalidateQueries({ queryKey: ["services"] });
  };

  return (
    <div>
      {isSuper && <button onClick={() => setEditing({ ...EMPTY_SERVICE })} data-testid="add-service-btn" className="btn-gold rounded-full px-5 py-2.5 text-sm font-mono-label uppercase flex items-center gap-2 mb-5"><Plus size={15} /> Add Service</button>}
      <p className="text-white/40 text-xs mb-3">Use the arrows to set the order clients see.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((s, i) => (
          <div key={s.id} className="bg-[#111] border border-white/10 rounded-2xl overflow-hidden flex" data-testid={`service-row-${i}`}>
            <div className="relative w-24 shrink-0 overflow-hidden"><CoverMedia item={s} playSize="sm" /></div>
            <div className="flex-1 p-4 flex justify-between">
              <div>
                <h3 className="font-display text-white">{s.title}</h3>
                <p className="flex items-center gap-1.5 text-white/40 text-xs"><span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: colorHex(s.calendar_color) }} data-testid={`service-color-dot-${i}`} />{s.category} · {s.price} {isSuper && s.hourly_rate ? `· $${s.hourly_rate}/hr` : ""}</p>
                {!s.active && <span className="text-[10px] text-white/40">inactive</span>}
              </div>
              <div className="flex gap-1 items-start">
                <div className="flex flex-col">
                  <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`service-up-${i}`} className="text-white/50 hover:text-[#D4AF37] disabled:opacity-20 p-0.5"><ArrowUp size={14} /></button>
                  <button onClick={() => move(i, 1)} disabled={i === items.length - 1} data-testid={`service-down-${i}`} className="text-white/50 hover:text-[#D4AF37] disabled:opacity-20 p-0.5"><ArrowDown size={14} /></button>
                </div>
                <button onClick={() => setEditing(withMedia(s))} data-testid={`edit-service-${i}`} className="text-white/50 hover:text-[#D4AF37] p-1"><Pencil size={14} /></button>
                {isSuper && <button onClick={() => remove(s.id)} data-testid={`delete-service-${i}`} className="text-white/50 hover:text-red-400 p-1"><Trash2 size={14} /></button>}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#111] border-white/15 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing?.id ? "Edit" : "New"} Service</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Input placeholder="Title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="bg-[#0a0a0a] border-white/15" data-testid="service-title" />
              <Input placeholder="Category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="bg-[#0a0a0a] border-white/15" />
              <Textarea placeholder="Description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="bg-[#0a0a0a] border-white/15" rows={3} />
              {isSuper && <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Price label (e.g. From $250)" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="bg-[#0a0a0a] border-white/15" data-testid="service-price" />
                <Input type="number" placeholder="Hourly rate" value={editing.hourly_rate} onChange={(e) => setEditing({ ...editing, hourly_rate: Number(e.target.value) })} className="bg-[#0a0a0a] border-white/15" data-testid="service-rate" />
              </div>}
              <ColorPicker value={editing.color_id || ""} auto={editing.auto_color} onChange={(c) => setEditing({ ...editing, color_id: c })} />
              <MediaEditor value={editing} onChange={(m) => setEditing({ ...editing, ...m })} />
              <div className="flex items-center gap-3"><Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} /><span className="text-sm text-white/70">Active</span></div>
              <button onClick={save} data-testid="save-service-btn" className="btn-gold w-full rounded-full py-3 text-sm font-mono-label uppercase">Save</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PortfolioTab() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data: items = [] } = useQuery({ queryKey: ["portfolio-admin"], queryFn: () => api.get("/portfolio").then((r) => r.data) });

  const save = async () => {
    try {
      const body = { ...editing, image: editing.image || (editing.images || [])[0] || "" };
      if (editing.id) await api.put(`/portfolio/${editing.id}`, body);
      else await api.post("/portfolio", body);
      qc.invalidateQueries({ queryKey: ["portfolio-admin"] });
      qc.invalidateQueries({ queryKey: ["portfolio"] });
      setEditing(null);
      toast.success("Saved");
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete project?")) return;
    await api.delete(`/portfolio/${id}`);
    qc.invalidateQueries({ queryKey: ["portfolio-admin"] });
    toast.success("Deleted");
  };
  const move = async (idx, dir) => {
    const arr = [...items];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    await api.post("/portfolio/reorder", arr.map((p) => p.id));
    qc.invalidateQueries({ queryKey: ["portfolio-admin"] });
    qc.invalidateQueries({ queryKey: ["portfolio"] });
  };

  return (
    <div>
      <button onClick={() => setEditing({ ...EMPTY_PORTFOLIO })} data-testid="add-portfolio-btn" className="btn-gold rounded-full px-5 py-2.5 text-sm font-mono-label uppercase flex items-center gap-2 mb-5"><Plus size={15} /> Add Project</button>
      <p className="text-white/40 text-xs mb-3">Use the arrows to set the order clients see.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        {items.map((p, i) => (
          <div key={p.id} className="bg-[#111] border border-white/10 rounded-2xl overflow-hidden" data-testid={`portfolio-row-${i}`}>
            <div className="relative h-32 overflow-hidden"><CoverMedia item={p} playSize="sm" />{p.featured && <Star className="absolute top-2 left-2 text-[#D4AF37] fill-[#D4AF37]" size={16} />}</div>
            <div className="p-3 flex justify-between items-center">
              <div className="min-w-0"><h3 className="font-display text-white text-sm truncate">{p.title}</h3><p className="text-white/40 text-xs">{p.category} · {(p.images || []).length} media</p></div>
              <div className="flex gap-1 items-center">
                <div className="flex flex-col">
                  <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`portfolio-up-${i}`} className="text-white/50 hover:text-[#D4AF37] disabled:opacity-20 p-0.5"><ArrowUp size={13} /></button>
                  <button onClick={() => move(i, 1)} disabled={i === items.length - 1} data-testid={`portfolio-down-${i}`} className="text-white/50 hover:text-[#D4AF37] disabled:opacity-20 p-0.5"><ArrowDown size={13} /></button>
                </div>
                <button onClick={() => setEditing(withMedia(p))} data-testid={`edit-portfolio-${i}`} className="text-white/50 hover:text-[#D4AF37] p-1"><Pencil size={14} /></button>
                <button onClick={() => remove(p.id)} data-testid={`delete-portfolio-${i}`} className="text-white/50 hover:text-red-400 p-1"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#111] border-white/15 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing?.id ? "Edit" : "New"} Project</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Input placeholder="Title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="bg-[#0a0a0a] border-white/15" data-testid="portfolio-title" />
              <Input placeholder="Category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="bg-[#0a0a0a] border-white/15" />
              <div className="flex items-center gap-3"><Switch checked={editing.featured} onCheckedChange={(v) => setEditing({ ...editing, featured: v })} /><span className="text-sm text-white/70">Featured</span></div>

              <MediaEditor value={editing} onChange={(m) => setEditing({ ...editing, ...m })} />

              <button onClick={save} data-testid="save-portfolio-btn" className="btn-gold w-full rounded-full py-3 text-sm font-mono-label uppercase">Save</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
